import { useDebouncedSearch, useLoading } from '@gorila-shared-ui/components';
import { OnChangeParams } from 'baseui/select';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useDebounceFunction } from '../../../hooks/useDebounce';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import { getFleetAssets } from '../../../services/fleetService';
import { apiParamsState } from '../../../storage/app';
import { AssetType } from '../../../types/asset';
import { FleetAsset } from '../../../types/fleet';
import { getAssetTitle } from '../../../utils/assets';
import { EnhancedLabeledSelect } from '../../ui/EnhancedLabeledSelect';

type Props = {
  asset?: FleetAsset;
  assetTypes?: AssetType[];
  selectedAssetId?: string;
  onChangeAssetId?: (assetId?: string) => void;
  onChangeAsset?: (asset?: FleetAsset) => void;
  label?: string;
  maxDropdownHeight?: string;
  placeholder?: string;
  emptyOptionText?: string;
  showEmptyOption?: boolean;
  helpText?: React.ReactNode;
  error?: React.ReactNode;
  defaultSearch?: string;
  required?: boolean;
  isFilter?: boolean;
  inline?: boolean;
};

function FleetAssetSelect({
  selectedAssetId,
  onChangeAssetId,
  onChangeAsset,
  asset,
  assetTypes,
  label = 'Activo:',
  showEmptyOption = false,
  placeholder = 'Selecciona un activo',
  emptyOptionText = 'Selecciona un activo',
  maxDropdownHeight,
  helpText,
  error,
  defaultSearch,
  isFilter = false,
  required = false,
  inline,
}: Readonly<Props>) {
  const apiParams = useRecoilValue(apiParamsState);
  const [assetList, setAssetList] = useState<FleetAsset[]>();
  const { loading, startLoading, stopLoading } = useLoading();
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const { search, setSearch, trigger: debouncedSearch } = useDebouncedSearch(defaultSearch ?? '');
  const [selectedAssetOption, setSelectedAssetOption] = useState<{
    id: string;
    label: string;
  }>();
  const requestIdRef = useRef(0);

  useUpdateEffect(() => {
    setPage(0);
  }, [debouncedSearch, apiParams?.clientId, apiParams?.subClientId]);

  useEffect(() => {
    if (!apiParams) return;
    if (page === 0) {
      setPage(1);
      return;
    }
    startLoading();
    const loadAssets = async () => {
      const requestId = ++requestIdRef.current;
      const { assets, error, hasNext } = await getFleetAssets({
        page,
        clientId: apiParams.clientId,
        subClientId: apiParams.subClientId,
        search,
        assetTypes,
      });
      if (requestId === requestIdRef.current) {
        if (!error && assets && hasNext !== undefined) {
          if (page > 1 && assetList) {
            setAssetList([...assetList, ...assets]);
            setHasNextPage(hasNext);
          } else {
            setAssetList(assets);
            setHasNextPage(hasNext);
          }
        } else {
          setAssetList(undefined);
          setHasNextPage(false);
        }
        stopLoading();
      }
    };
    loadAssets();
  }, [page]);

  const assetOptions = useMemo(() => {
    if (!assetList) return;
    const assetsOptions: { id: string | undefined; label: string }[] = assetList.map((asset) => ({
      id: asset._id,
      label: `${getAssetTitle(asset)} (${asset.label})`,
    }));
    if (showEmptyOption) {
      assetsOptions?.unshift({
        id: undefined,
        label: isFilter ? 'Todos' : emptyOptionText,
      });
    }
    if (asset) {
      if (!assetsOptions.find((c) => c.id === asset._id))
        assetsOptions.push({
          id: asset._id,
          label: `${getAssetTitle(asset)} (${asset.label})`,
        });
    }
    if (selectedAssetOption) {
      if (!assetsOptions.find((c) => c.id === selectedAssetOption.id)) assetsOptions.push(selectedAssetOption);
    }
    return assetsOptions;
  }, [asset, assetList, emptyOptionText, isFilter, selectedAssetOption, showEmptyOption]);

  const onAssetChange = (params: OnChangeParams) => {
    if (params.option?.id) {
      onChangeAssetId?.(params.option.id as string);
      if (onChangeAsset) {
        const asset = assetList?.find((asset) => asset._id === (params.option?.id as string));
        onChangeAsset(asset);
      }
    } else {
      onChangeAssetId?.(undefined);
      onChangeAsset?.(undefined);
    }
    setSelectedAssetOption(params.option as { id: string; label: string });
    setSearch(undefined);
  };

  const fetchData = () => {
    if (hasNextPage) setPage(page + 1);
  };

  const handleInputChange = useDebounceFunction(function (term: string) {
    setSearch(term);
  }, 350);

  return (
    <EnhancedLabeledSelect
      label={label}
      options={assetOptions}
      value={showEmptyOption || selectedAssetId !== undefined ? [{ id: selectedAssetId }] : undefined}
      onChange={onAssetChange}
      onInputChange={(e) => {
        handleInputChange(e.target.value);
      }}
      infiniteScroll
      inline={inline}
      inset={inline}
      fetchData={fetchData}
      fetchDataLoading={loading}
      hasNext={hasNextPage}
      placeholder={placeholder}
      searchable
      maxDropdownHeight={maxDropdownHeight}
      defaultSearch={defaultSearch}
      onBlurResetsInput={false}
      caption={helpText}
      error={error}
      required={required}
    />
  );
}

export default FleetAssetSelect;
